import {
  BILINGUAL_LIMITS,
  localeToContentLang,
  otherLang,
  type ContentLang,
} from '@yoyoclub/shared';
import { Bold, ImagePlus, Link2, List, Loader2 } from 'lucide-react';
import { useId, useRef, useState, type MouseEvent } from 'react';

import { useSiteLanguage } from '../i18n/useSiteLanguage';

import { BilingualField } from './BilingualField';
import { DescriptionMarkdown } from './DescriptionMarkdown';
import { applySideEdit, type BilingualValue } from './bilingualValue';
import { applyBold, applyLink, applyList, insertSnippet, type TextEdit } from './markdownEdits';

const SIDE_KEY = { ZH: 'zh', EN: 'en' } as const;
const SIDE_LABEL: Record<ContentLang, string> = { ZH: 'Chinese', EN: 'English' };

const TOOL_BUTTON =
  'inline-flex h-11 sm:h-9 cursor-pointer items-center gap-1.5 rounded-md px-3 text-sm text-gray-700 transition-colors hover:bg-white hover:text-gray-900 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary disabled:cursor-not-allowed disabled:opacity-40';

interface Props {
  fieldType: 'activityDescription' | 'courseDescription' | 'passDescription';
  /** Field name shown above the toolbar, e.g. "Description". */
  label: string;
  value: BilingualValue;
  onChange: (next: BilingualValue) => void;
  rows?: number;
  placeholder?: Partial<Record<ContentLang, string>>;
  inputClassName?: string;
  /** Uploads a picture and resolves to its public URL. Omit to hide "Add photo". */
  uploadImage?: (file: File) => Promise<string>;
  onUploadingChange?: (uploading: boolean) => void;
  onUploadError?: (message: string) => void;
  /** Match the family page: true where single line breaks are kept (course page). */
  lineBreaks?: boolean;
  className?: string;
  [dataAttr: `data-${string}`]: string | undefined;
}

/**
 * A bilingual description with one toolbar for both languages: bold, list,
 * link and photo insert into whichever side the host last typed in, and
 * Preview renders both sides exactly as families see them. The stored value
 * stays plain Markdown/HTML text.
 */
export function DescriptionEditor({
  fieldType,
  label,
  value,
  onChange,
  rows = 6,
  placeholder,
  inputClassName,
  uploadImage,
  onUploadingChange,
  onUploadError,
  lineBreaks = false,
  className = '',
  ...rest
}: Props) {
  const idBase = useId();
  // The user's language comes first; the preview shows Chinese in their script (PRD §4.1, §5.2).
  const siteLanguage = useSiteLanguage();
  const firstSide = localeToContentLang(siteLanguage);
  const zhRef = useRef<HTMLTextAreaElement>(null);
  const enRef = useRef<HTMLTextAreaElement>(null);
  const refs = { ZH: zhRef, EN: enRef };
  const fileInput = useRef<HTMLInputElement>(null);
  // Side the toolbar acts on: the one last focused (see targetSide).
  const focusedSide = useRef<ContentLang | null>(null);
  // Latest value, for edits applied after an upload await.
  const valueRef = useRef(value);
  valueRef.current = value;

  const [preview, setPreview] = useState(false);
  const [uploading, setUploading] = useState(false);
  const [notice, setNotice] = useState<string | null>(null);

  /** Keep the textarea focused so toolbar clicks do not trigger blur-translate. */
  const keepFocus = (e: MouseEvent) => e.preventDefault();

  const restoreSelection = (side: ContentLang, start: number, end: number) => {
    requestAnimationFrame(() => {
      const ta = refs[side].current;
      if (!ta) return;
      ta.focus();
      ta.setSelectionRange(start, end);
    });
  };

  const selectionOf = (side: ContentLang, text: string) => {
    const ta = refs[side].current;
    return ta ? [ta.selectionStart, ta.selectionEnd] : [text.length, text.length];
  };

  const limit = (side: ContentLang) => BILINGUAL_LIMITS[fieldType][side];
  const overLimit = (side: ContentLang) =>
    `The ${SIDE_LABEL[side]} text is at its ${limit(side)} character limit. Shorten it first.`;

  /**
   * The last focused side. Before any focus, the side the host wrote, so markup
   * never lands in an empty language and hides the fallback to the other one.
   */
  const targetSide = (): ContentLang => {
    if (focusedSide.current) return focusedSide.current;
    // Before any focus (PRD §4.1): the only side with the host's own text, else the
    // user's language. Markup never goes into a side whose emptiness the
    // fallback relies on.
    const v = valueRef.current;
    const written = (side: ContentLang) => v[SIDE_KEY[side]].trim() !== '' && v.autoLang !== side;
    const other = otherLang(firstSide);
    if (written(other) && !written(firstSide)) return other;
    return firstSide;
  };

  const runEdit = (edit: (text: string, start: number, end: number) => TextEdit) => {
    const side = targetSide();
    const current = valueRef.current;
    const text = current[SIDE_KEY[side]];
    const [start, end] = selectionOf(side, text);
    const result = edit(text, start, end);
    // Markup can push a side past the limit the textarea enforces for typing.
    if (result.text.length > limit(side)) {
      setNotice(overLimit(side));
      return;
    }
    setNotice(null);
    onChange(applySideEdit(current, side, result.text));
    restoreSelection(side, result.selStart, result.selEnd);
  };

  const addPhoto = async (file: File) => {
    if (!uploadImage) return;
    const side = targetSide();
    setUploading(true);
    onUploadingChange?.(true);
    setNotice(null);
    try {
      const url = await uploadImage(file);
      const tag = `<img src="${url}" alt="image" style="max-width:200px;width:100%" />`;
      const current = valueRef.current;
      const text = current[SIDE_KEY[side]];
      const [start, end] = selectionOf(side, text);
      const result = insertSnippet(text, start, end, tag);
      // The other side gets the photo too. Host-written text gets it appended at
      // the end; an empty or machine side picks it up when it is translated.
      const other = otherLang(side);
      const otherText = current[SIDE_KEY[other]];
      const appendToOther = otherText.trim() !== '' && current.autoLang !== other;
      const appended = `${otherText.trimEnd()}\n\n${tag}`;
      // Both sides or neither (PRD §4.1).
      if (result.text.length > limit(side)) {
        setNotice(overLimit(side));
        return;
      }
      if (appendToOther && appended.length > limit(other)) {
        setNotice(overLimit(other));
        return;
      }
      let next = applySideEdit(current, side, result.text);
      if (appendToOther) {
        next = { ...next, [SIDE_KEY[other]]: appended };
        setNotice(`Also added at the end of the ${SIDE_LABEL[other]} text. Move it if needed.`);
      }
      onChange(next);
      restoreSelection(side, result.selStart, result.selEnd);
    } catch (err) {
      onUploadError?.(err instanceof Error ? err.message : 'Failed to upload image');
    } finally {
      setUploading(false);
      onUploadingChange?.(false);
    }
  };

  const labelId = `${idBase}-label`;

  return (
    <div className={className} role="group" aria-labelledby={labelId} {...rest}>
      <p id={labelId} className="mb-1 block text-sm font-medium text-gray-700">
        {label}
      </p>

      <div
        role="toolbar"
        aria-label={`${label} formatting`}
        className="mb-3 flex flex-wrap items-center gap-1 rounded-lg border border-gray-200 bg-gray-50 p-1"
      >
        <button
          type="button"
          className={TOOL_BUTTON}
          onMouseDown={keepFocus}
          onClick={() => runEdit(applyBold)}
          disabled={preview}
        >
          <Bold className="h-4 w-4" aria-hidden="true" />
          Bold
        </button>
        <button
          type="button"
          className={TOOL_BUTTON}
          onMouseDown={keepFocus}
          onClick={() => runEdit(applyList)}
          disabled={preview}
        >
          <List className="h-4 w-4" aria-hidden="true" />
          List
        </button>
        <button
          type="button"
          className={TOOL_BUTTON}
          onMouseDown={keepFocus}
          onClick={() => runEdit(applyLink)}
          disabled={preview}
        >
          <Link2 className="h-4 w-4" aria-hidden="true" />
          Link
        </button>
        {uploadImage && (
          <>
            <button
              type="button"
              className={TOOL_BUTTON}
              onMouseDown={keepFocus}
              onClick={() => fileInput.current?.click()}
              disabled={preview || uploading}
            >
              {uploading ? (
                <Loader2 className="h-4 w-4 animate-spin" aria-hidden="true" />
              ) : (
                <ImagePlus className="h-4 w-4" aria-hidden="true" />
              )}
              {uploading ? 'Uploading...' : 'Add photo to text'}
            </button>
            <input
              ref={fileInput}
              type="file"
              accept="image/*"
              className="hidden"
              tabIndex={-1}
              aria-hidden="true"
              onChange={(e) => {
                const file = e.target.files?.[0];
                e.target.value = '';
                if (file) void addPhoto(file);
              }}
            />
          </>
        )}

        {/* Same size and inset as the tool buttons, so every button sits evenly inside the toolbar border. */}
        <div className="ml-auto flex items-center gap-1" role="group" aria-label="View">
          {[false, true].map((isPreview) => (
            <button
              key={String(isPreview)}
              type="button"
              aria-pressed={preview === isPreview}
              onClick={() => setPreview(isPreview)}
              className={`inline-flex h-11 sm:h-9 cursor-pointer items-center rounded-md px-3 text-sm transition-colors focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary ${
                preview === isPreview
                  ? 'bg-primary text-white'
                  : 'text-gray-700 hover:bg-white hover:text-gray-900'
              }`}
            >
              {isPreview ? 'Preview' : 'Write'}
            </button>
          ))}
        </div>
      </div>

      {/* Kept mounted while previewing so pending translations still land. */}
      <div className={preview ? 'hidden' : undefined}>
        <BilingualField
          fieldType={fieldType}
          label={label}
          multiline
          rows={rows}
          value={value}
          onChange={(next) => {
            setNotice(null);
            onChange(next);
          }}
          placeholder={placeholder}
          inputClassName={inputClassName}
          inputRefs={refs}
          onFocusSide={(side) => {
            focusedSide.current = side;
          }}
          sideLabels={{ ZH: 'Chinese', EN: 'English' }}
        />
      </div>

      {preview && (
        <div className="grid grid-cols-1 gap-6 md:grid-cols-2">
          {[firstSide, otherLang(firstSide)].map((side) => {
            const text = value[SIDE_KEY[side]].trim();
            return (
              <div key={side} className="min-w-0">
                <p className="mb-1 text-sm font-medium text-gray-700">{SIDE_LABEL[side]}</p>
                <div className="min-h-[9rem] rounded-lg border border-gray-200 bg-white px-4 py-3 text-sm text-gray-700">
                  {text ? (
                    // The English pane renders as English readers see it, Chinese untouched.
                    <DescriptionMarkdown
                      lineBreaks={lineBreaks}
                      lang={side === 'ZH' ? siteLanguage : 'en'}
                    >
                      {text}
                    </DescriptionMarkdown>
                  ) : (
                    <p className="text-gray-400">Nothing written in {SIDE_LABEL[side]} yet.</p>
                  )}
                </div>
              </div>
            );
          })}
        </div>
      )}

      <p className="mt-2 text-sm text-amber-700 empty:mt-0" aria-live="polite">
        {notice}
      </p>
      <div className="mt-1 text-xs text-gray-500">
        <p>
          Write in either language and we will translate it into the other. Plain text, Markdown and
          HTML are supported.
        </p>
      </div>
    </div>
  );
}
